import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../Context/AuthContext';
import { GetCountries, GetState } from 'react-country-state-city';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import { toast } from 'react-toastify';
import CustomDatePicker from '../Common/CustomDatePicker';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const YES_NO_OPTIONS = [
    { id: true, label: 'Yes' },
    { id: false, label: 'No' },
];

const COVERAGE_TYPE_SETTING_ID = 55;

const mapProviderSettingItem = (item) => ({
    value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
    label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

const emptyForm = {
    id: null,
    provider_id: null,
    status_id: null,
    file_id: null,
    insurer_name: '',
    policy: '',
    is_primary_policy: null,
    current_carrier: null,
    insurer_state: '',
    policy_effective_date: null,
    policy_expiration_date: null,
    retroactive_date: null,
    coverage_type: '',
    carrier_years: '',
    incident_amount: '',
    aggregate_amount: '',
    unlimited_coverage: null,
    organization_insured: '',
    statutory_limit: null,
    contact_id: null,
    document: null,
    contact: {
        contact_name: '',
        contact_title: '',
        contact_email: '',
        contact_phone: '',
        contact_fax: '',
    },
    type: 'create',
};

const validationSchema = Yup.object({
    insurer_name: Yup.string().trim().nullable(),
    policy: Yup.string().trim().nullable(),
    is_primary_policy: Yup.boolean().nullable(),
    current_carrier: Yup.boolean().nullable(),
    insurer_state: Yup.string().trim().nullable(),
    policy_effective_date: Yup.date().nullable().typeError('Invalid date format'),
    policy_expiration_date: Yup.date().nullable().typeError('Invalid date format'),
    retroactive_date: Yup.date().nullable().typeError('Invalid date format'),
    coverage_type: Yup.number().nullable(),
    carrier_years: Yup.number().nullable(),
    incident_amount: Yup.string().trim().nullable(),
    aggregate_amount: Yup.string().trim().nullable(),
    unlimited_coverage: Yup.boolean().nullable(),
    organization_insured: Yup.string().trim().nullable(),
    statutory_limit: Yup.boolean().nullable(),
    document: Yup.mixed().nullable(),
    contact: Yup.object({
        contact_name: Yup.string().trim().nullable(),
        contact_title: Yup.string().trim().nullable(),
        contact_email: Yup.string().trim().nullable().email('Invalid email'),
        contact_phone: Yup.string().trim().nullable(),
        contact_fax: Yup.string().trim().nullable(),
    }).nullable(),
    type: Yup.string().trim().nullable(),
});

const getBooleanValue = (value) =>
    value === true ? 'true' : value === false ? 'false' : '';

export default function MedicalMalpracticeModal({
    isOpen,
    providerId,
    onClose,
    onSave,
    initialData,
    isLoading,
}) {
    const { token } = useAuth();
    const [licenseStateOptions, setLicenseStateOptions] = useState([]);
    const [licenseStateLoading, setLicenseStateLoading] = useState(false);
    const [coverageTypeOptions, setCoverageTypeOptions] = useState([]);
    const [coverageTypeLoading, setCoverageTypeLoading] = useState(false);

    const initialValues = useMemo(() => {
        const formBase = initialData
            ? { ...emptyForm, ...initialData }
            : { ...emptyForm, provider_id: providerId };

        return {
            ...formBase,
            provider_id: formBase.provider_id || providerId || null,
            document:
                initialData?.document || initialData?.file_name || initialData?.fileName || null,
            contact: {
                ...emptyForm.contact,
                ...(initialData?.contact || {}),
            },
            type: initialData?.type || (initialData?.id ? 'update' : 'create'),
        };
    }, [initialData, providerId]);

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            const payload = {
                ...values,
                id: values.id || null,
                coverage_type: values.coverage_type !== '' && values.coverage_type !== null && values.coverage_type !== undefined ? Number(values.coverage_type) : null,
                carrier_years: values.carrier_years !== '' && values.carrier_years !== null && values.carrier_years !== undefined ? Number(values.carrier_years) : null,
                is_primary_policy: values.is_primary_policy === true || values.is_primary_policy === 'true' ? true : values.is_primary_policy === false || values.is_primary_policy === 'false' ? false : null,
                current_carrier: values.current_carrier === true || values.current_carrier === 'true' ? true : values.current_carrier === false || values.current_carrier === 'false' ? false : null,
                unlimited_coverage: values.unlimited_coverage === true || values.unlimited_coverage === 'true' ? true : values.unlimited_coverage === false || values.unlimited_coverage === 'false' ? false : null,
                statutory_limit: values.statutory_limit === true || values.statutory_limit === 'true' ? true : values.statutory_limit === false || values.statutory_limit === 'false' ? false : null,
                policy_effective_date: values.policy_effective_date || null,
                policy_expiration_date: values.policy_expiration_date || null,
                retroactive_date: values.retroactive_date || null,
                type: values.id ? 'update' : 'create',
            };
            onSave(payload);
        },
    });

    const loadLicenseStateOptions = async () => {
        try {
            setLicenseStateLoading(true);
            const countries = await GetCountries();
            const unitedStates = Array.isArray(countries)
                ? countries.find(
                      (country) =>
                          ['united states', 'us', 'usa'].includes(
                              String(country.name ?? '').trim().toLowerCase()
                          ) ||
                          String(country.iso2 ?? '').toUpperCase() === 'US' ||
                          String(country.iso3 ?? '').toUpperCase() === 'USA'
                  )
                : null;
            const countryId =
                unitedStates?.id ?? unitedStates?.iso2 ?? unitedStates?.iso3 ?? 'US';
            const stateList = await GetState(countryId);
            if (Array.isArray(stateList)) {
                setLicenseStateOptions(
                    stateList.map((state) => ({
                        value: state.name ?? state.state_code ?? state.code ?? '',
                        label: state.name ?? state.state_code ?? state.code ?? '',
                        key: state.id ?? state.state_code ?? state.name,
                    }))
                );
            }
        } catch (error) {
            console.error('Failed to load license state options:', error);
            setLicenseStateOptions([]);
        } finally {
            setLicenseStateLoading(false);
        }
    };

    const loadCoverageTypeOptions = async () => {
        if (!token) return;
        try {
            setCoverageTypeLoading(true);
            const response = await getAllProviderSettings(token, COVERAGE_TYPE_SETTING_ID);
                if (response?.is_success && Array.isArray(response.data)) {
                    setCoverageTypeOptions(response.data.map(mapProviderSettingItem));
                } else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal Server Error. Please Contact Support Team.');
                    setCoverageTypeOptions([]);
                } else {
                    //toast.error(response?.message || 'Failed to load coverage type settings');
                    setCoverageTypeOptions([]);
                }
        } catch (error) {
            console.error('Failed to load coverage type settings:', error);
            setCoverageTypeOptions([]);
        } finally {
            setCoverageTypeLoading(false);
        }
    };

    useEffect(() => {
        loadLicenseStateOptions();
        loadCoverageTypeOptions();
    }, [token]);

    if (!isOpen) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
                        <h5 className="modal-title fw-bold hc-text-primary m-0">
                            {initialData?.id ? 'Edit Malpractice Insurance' : 'Add Malpractice Insurance'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                        <input type="hidden" name="provider_id" value={formik.values.provider_id || ''} />
                        <input type="hidden" name="status_id" value={formik.values.status_id || ''} />
                        <input type="hidden" name="file_id" value={formik.values.file_id || ''} />
                        <input type="hidden" name="contact_id" value={formik.values.contact_id || ''} />
                        <input type="hidden" name="type" value={formik.values.type || ''} />

                        <div className="modal-body px-4 py-2">
                            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                                <h6 className="fw-semibold hc-text-primary mb-3">Insurance Details</h6>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Insurer Name</label>
                                        <input
                                            {...formik.getFieldProps('insurer_name')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter insurer name"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Policy</label>
                                        <input
                                            {...formik.getFieldProps('policy')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter policy number or name"
                                        />
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <SearchableDropdown
                                            label="Insurer State"
                                            placeholder="Select Option"
                                            options={licenseStateOptions}
                                            value={formik.values.insurer_state || ''}
                                            onChange={(value) => formik.setFieldValue('insurer_state', value || '')}
                                            disabled={licenseStateLoading && !licenseStateOptions.length}
                                            loading={licenseStateLoading && !licenseStateOptions.length}
                                            emptyMessage="No license states found"
                                            allowCustom={true}
                                        />
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <SearchableDropdown
                                            label="Coverage Type"
                                            placeholder="Select Option"
                                            options={coverageTypeOptions}
                                            value={formik.values.coverage_type ?? ''}
                                            onChange={(value) => formik.setFieldValue('coverage_type', value ?? '')}
                                            disabled={coverageTypeLoading && !coverageTypeOptions.length}
                                            loading={coverageTypeLoading && !coverageTypeOptions.length}
                                            emptyMessage="No coverage types found"
                                        />
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Carrier Years</label>
                                        <input
                                            type="number"
                                            min="0"
                                            {...formik.getFieldProps('carrier_years')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter number of years"
                                        />
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Policy Effective Date</label>
                                        <CustomDatePicker
                                        formik={formik}
                                        {...formik.getFieldProps('policy_effective_date')}
                                        />
                                        {/* <input
                                            type="date"
                                            {...formik.getFieldProps('policy_effective_date')}
                                            className="form-control rounded-3 shadow-sm"
                                        /> */}
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Policy Expiration Date</label>
                                        <CustomDatePicker
                                        formik={formik}
                                        {...formik.getFieldProps('policy_expiration_date')}
                                        />
                                        {/* <input
                                            type="date"
                                            {...formik.getFieldProps('policy_expiration_date')}
                                            className="form-control rounded-3 shadow-sm"
                                        /> */}
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Retroactive Date</label>
                                        <CustomDatePicker
                                        formik={formik}
                                        {...formik.getFieldProps('retroactive_date')}
                                        />
                                        {/* <input
                                            type="date"
                                            {...formik.getFieldProps('retroactive_date')}
                                            className="form-control rounded-3 shadow-sm"
                                        /> */}
                                    </div>

                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Incident Amount</label>
                                        <input
                                            {...formik.getFieldProps('incident_amount')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter incident amount"
                                        />
                                    </div>
                                    <div className="col-md-6 col-lg-4">
                                        <label className="form-label fw-semibold">Aggregate Amount</label>
                                        <input
                                            {...formik.getFieldProps('aggregate_amount')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter aggregate amount"
                                        />
                                    </div>

                                    <div className="col-lg-4">
                                        <label className="form-label fw-semibold">Organization Insured</label>
                                        <input
                                            {...formik.getFieldProps('organization_insured')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter organization insured"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                                <h6 className="fw-semibold hc-text-primary mb-3">Policy Flags</h6>
                                <div className="row g-3">
                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label fw-semibold">Primary Policy</label>
                                        <select
                                            name="is_primary_policy"
                                            value={getBooleanValue(formik.values.is_primary_policy)}
                                            onChange={(event) => formik.setFieldValue('is_primary_policy', event.target.value === 'true')}
                                            className="form-select rounded-3 shadow-sm"
                                        >
                                            <option value="">Select Option</option>
                                            {YES_NO_OPTIONS.map((option) => (
                                                <option key={String(option.id)} value={String(option.id)}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label fw-semibold">Current Carrier</label>
                                        <select
                                            name="current_carrier"
                                            value={getBooleanValue(formik.values.current_carrier)}
                                            onChange={(event) => formik.setFieldValue('current_carrier', event.target.value === 'true')}
                                            className="form-select rounded-3 shadow-sm"
                                        >
                                            <option value="">Select Option</option>
                                            {YES_NO_OPTIONS.map((option) => (
                                                <option key={String(option.id)} value={String(option.id)}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label fw-semibold">Unlimited Coverage</label>
                                        <select
                                            name="unlimited_coverage"
                                            value={getBooleanValue(formik.values.unlimited_coverage)}
                                            onChange={(event) => formik.setFieldValue('unlimited_coverage', event.target.value === 'true')}
                                            className="form-select rounded-3 shadow-sm"
                                        >
                                            <option value="">Select Option</option>
                                            {YES_NO_OPTIONS.map((option) => (
                                                <option key={String(option.id)} value={String(option.id)}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6 col-lg-3">
                                        <label className="form-label fw-semibold">Statutory Limit</label>
                                        <select
                                            name="statutory_limit"
                                            value={getBooleanValue(formik.values.statutory_limit)}
                                            onChange={(event) => formik.setFieldValue('statutory_limit', event.target.value === 'true')}
                                            className="form-select rounded-3 shadow-sm"
                                        >
                                            <option value="">Select Option</option>
                                            {YES_NO_OPTIONS.map((option) => (
                                                <option key={String(option.id)} value={String(option.id)}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                                <h6 className="fw-semibold hc-text-primary mb-3">Document</h6>
                                <DocumentUpload
                                    id="medical-malpractice-document-input"
                                    label="Upload Document"
                                    value={formik.values.document}
                                    onChange={(file) => formik.setFieldValue('document', file)}
                                />
                            </div>

                            <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                                <h6 className="fw-semibold hc-text-primary mb-3">Contact Information</h6>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Contact Name</label>
                                        <input
                                            {...formik.getFieldProps('contact.contact_name')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter contact name"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Contact Title</label>
                                        <input
                                            {...formik.getFieldProps('contact.contact_title')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter contact title"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Contact Email</label>
                                        <input
                                            type="email"
                                            {...formik.getFieldProps('contact.contact_email')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter contact email"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Contact Phone</label>
                                        {/* <CustomPhoneInput
                                            formik={formik}
                                            name="contact.contact_phone"
                                            {...formik.getFieldProps('contact.contact_phone')}
                                            defaultCountry='us'
                                        /> */}
                                        <input
                                            {...formik.getFieldProps('contact.contact_phone')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter contact phone"
                                        />
                                    </div>

                                    <div className="col-lg-6">
                                        <label className="form-label fw-semibold">Contact Fax</label>
                                        <input
                                            {...formik.getFieldProps('contact.contact_fax')}
                                            className="form-control rounded-3 shadow-sm"
                                            placeholder="Enter contact fax"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </button>
                            <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2 text-white" role="status" aria-hidden="true" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
